import React, { ReactElement, FunctionComponent } from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { Button } from '@empiriecom/module-components/Button';
import getIcon from '@empiriecom/module-components/utils/getIcon';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import { FragmentConfiguration } from '@/config';
import { RatingReview, SubmitRatingResponse } from '@/src/interfaces/Rating';
import {
  createPreviewRating,
  createSubmitRating,
} from '@/src/components/CustomerRatings/CreateRating/utils';
import useUrlParameters from '@/src/components/hooks/useUrlParameters';
import { useProduct } from '@/src/components/ProductContext';
import RatingPreviewCard from './RatingPreviewCard';
import { useCreateRatingContext, CreateRatingStep } from '../CreateRatingContext';

const CreateRatingPreviewWrapper = styled.div`
  position: relative;
`;

const PreviewCardWrapper = styled.div`
  margin-top: 1.75rem;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const EditRatingIconWrapper = styled.span`
  margin-left: 0.75rem;
`;

const EditButton = styled(Button)`
  width: 13.75rem;
`;

const SumbitButton = styled(Button)`
  margin-top: 1rem;
  width: 13.75rem;
`;

const handleSubmitRatingClick = async (
  submitRatingURL: string,
  setIsModalOpen: (isOpen: boolean) => void,
  getRatingValue: () => number,
  getAdvantageTags: () => string[],
  getDisadvantageTags: () => string[],
  getRatingHeadline: () => string,
  getRatingReview: () => string,
  getReviewerCity: () => string,
  getReviewerName: () => string,
  setCreationStep: (step: CreateRatingStep) => void,
  productId: string,
) => {
  const ratingToSubmit = createSubmitRating(
    productId,
    getRatingValue(),
    getRatingHeadline(),
    getRatingReview(),
    getReviewerName(),
    getReviewerCity(),
    getAdvantageTags(),
    getDisadvantageTags(),
  );

  fetch(submitRatingURL, {
    body: JSON.stringify(ratingToSubmit),
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    method: 'POST',
  })
    .then((resp: Response) => {
      if (resp.ok) {
        return resp.json() as Promise<SubmitRatingResponse>;
      }
      setCreationStep('SUBMIT_ERROR');
      setIsModalOpen(false);
      return {};
    })
    .then((data: SubmitRatingResponse) => {
      if (data.success) {
        setCreationStep('SUBMIT_SUCCESS');
      } else {
        setCreationStep('SUBMIT_ERROR');
      }
      setIsModalOpen(false);
    });
};

interface CreateRatingPreviewProps {
  setIsModalOpen: (isOpen: boolean) => void;
}

export const CreateRatingPreview: FunctionComponent<CreateRatingPreviewProps> = ({
  setIsModalOpen,
}): ReactElement => {
  const {
    getRatingValue,
    getAdvantageTags,
    getDisadvantageTags,
    getRatingHeadline,
    getRatingReview,
    getReviewerCity,
    getReviewerName,
    setCreationStep,
  } = useCreateRatingContext();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { product } = useProduct();

  const config: FragmentConfiguration = useConfig();
  const submitRatingURL = useUrlParameters(
    config.customerRatings.values.submitRatingURL,
    undefined,
    true,
  );

  const previewRating: RatingReview = createPreviewRating(
    getReviewerName(),
    getReviewerCity(),
    getRatingValue(),
    getRatingReview(),
    getRatingHeadline(),
    getAdvantageTags(),
    getDisadvantageTags(),
  );

  const Pencil = getIcon('Edit');

  return (
    <CreateRatingPreviewWrapper data-testid="rating-preview">
      <FormattedMessage
        id="ratings.new-rating.preview-headline"
        defaultMessage="Hier kannst du deine Bewertung nochmal überprüfen"
      />
      <PreviewCardWrapper>
        <RatingPreviewCard rating={previewRating} />
      </PreviewCardWrapper>
      <ButtonWrapper>
        <EditButton
          data-testid="edit-rating-button"
          layout="default"
          onClick={() => setCreationStep('CREATE')}
          uppercase={false}
        >
          <FormattedMessage
            id="ratings.new-rating.edit-rating"
            defaultMessage="Bewertung bearbeiten"
          />
          <EditRatingIconWrapper>
            <Pencil size="1.25rem" />
          </EditRatingIconWrapper>
        </EditButton>
        <SumbitButton
          data-testid="submit-rating-button"
          layout="primary"
          onClick={() =>
            handleSubmitRatingClick(
              submitRatingURL,
              setIsModalOpen,
              getRatingValue,
              getAdvantageTags,
              getDisadvantageTags,
              getRatingHeadline,
              getRatingReview,
              getReviewerCity,
              getReviewerName,
              setCreationStep,
              product?.akl,
            )
          }
          uppercase={false}
        >
          <FormattedMessage
            id="ratings.new-rating.submit-rating"
            defaultMessage="Bewertung abgeben"
          />
        </SumbitButton>
      </ButtonWrapper>
    </CreateRatingPreviewWrapper>
  );
};

export default CreateRatingPreview;
